import type { ReactNode } from 'react';
import { formatCount } from '../time';
import { asLines } from './Lines';

/**
 * 빈 상태·오류·읽는 중 (19회차, 콘솔 검토 A-4(g)).
 *
 * 16회차까지 로딩 문구가 네 가지('읽는 중…'·'불러오는 중…'·'찾는 중…'·'변환 중…'),
 * 오류 표기가 여섯 곳, 빈 상태가 여덟 곳에 제각각 있었다. 여백도 화면마다 달랐다.
 *
 * 빈 상태는 **왜 비었는지**를 같이 적는다. "자료가 없다"만 있으면 에이전트가 안 붙은 것인지
 * 정말 조용한 것인지 구분할 수 없다.
 */

export function EmptyState({ title, hint }: { title: string; hint?: ReactNode }) {
  return (
    <div className="rounded-xl border border-hair bg-panel px-6 py-8 text-center">
      <p className="whitespace-pre-line break-words text-sm text-muted">{title}</p>
      {hint ? (
        <p className="mt-1 whitespace-pre-line break-words text-xs text-muted">{asLines(hint)}</p>
      ) : null}
    </div>
  );
}

/**
 * 서버 상한에 잘린 표의 안내 한 줄 (23-A회차, UI 피드백 18).
 *
 * 표는 페이징을 두지 않고 안에서만 스크롤한다(`Table`의 높이 상한). 그래서 스크롤을 끝까지
 * 내린 사람이 "이게 전부"라고 믿게 된다. 서버가 상위 N건만 준 표에는 그렇지 않다고 적는다.
 * 잘리지 않았으면 아무것도 그리지 않는다.
 *
 * **행 상한과 조회 상한은 다른 것이다** (37회차 리뷰 R37-05). 행 상한은 "센 것 중 상위 N만
 * 보인다"이고, 조회 상한(`truncated`)은 "다 세지도 못했다"이다. 뒤엣것은 표의 숫자 자체가
 * 실제보다 작다는 뜻이라 행 상한에 안 걸려도 알려야 한다. 문구는 화면이 준다 —
 * 이 부품은 `terms.ts`를 모른다.
 */
export function LimitNote({
  shown,
  total,
  unit = '건',
  hint = '조건을 좁히거나 정렬 기준을 바꿔 볼 것',
  truncated = false,
  truncatedNote,
}: {
  /** 표에 실제로 그린 행 수 */
  shown: number;
  /** 서버가 센 전체 수. `shown`보다 크면 잘린 것이다 */
  total: number;
  /** 세는 단위. 진입점은 '개', 요청은 '건' */
  unit?: string;
  hint?: string;
  /** 서버가 조회 상한에 걸려 다 못 봤다. 참이면 행 상한에 안 걸려도 한 줄을 낸다 */
  truncated?: boolean;
  /** 그때 적을 문장. 화면이 준다 */
  truncatedNote?: string;
}) {
  const limited = total > shown;
  if (!limited && !truncated) return null;
  return (
    <p className="tabular mt-3 text-xs text-muted">
      {limited ? (
        <span className="block">
          전체 {formatCount(total)}
          {unit} 중 상위 {formatCount(shown)}
          {unit}만 표시한다.
        </span>
      ) : null}
      {truncated && truncatedNote !== undefined ? (
        <span className="block whitespace-pre-line text-fail">{truncatedNote}</span>
      ) : null}
      {limited ? <span className="block">{hint}</span> : null}
    </p>
  );
}

export function ErrorText({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="break-words rounded-lg bg-error/10 px-3 py-2 text-sm text-error">
      {children}
    </p>
  );
}

/** 읽는 중. 문구는 한 가지만 쓴다 */
export function Loading({ label = '읽는 중' }: { label?: string }) {
  return (
    <p aria-live="polite" className="py-2 text-sm text-muted">
      {label}…
    </p>
  );
}

/**
 * 어려운 말에 붙이는 한 줄 설명 (콘솔 검토 C-19).
 *
 * 용어를 아예 쉬운 말로 바꾸는 것이 먼저고(`terms.ts`), 그래도 남는 것에만 붙인다.
 * `<abbr>`은 스크린리더가 `title`을 읽어 주고, 점선 밑줄로 눌러 볼 것이 있다고 알린다.
 */
export function Tooltip({ text, children }: { text: string; children: ReactNode }) {
  return (
    <abbr
      title={text}
      className="cursor-default underline decoration-hair decoration-dotted underline-offset-4"
    >
      {children}
    </abbr>
  );
}
